import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATES = ["pending", "requires_review", "verified", "rejected", "requires_resubmission"];

export default function HQVerification() {
  const [state, setState] = useState("pending");
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState({});

  const load = () => api.get(`/hq/verification?state=${state}`).then((r) => setItems(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [state]);

  const act = async (uid, action) => {
    const r = reason[uid] || "";
    if (action !== "mark_verified" && !r.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/hq/verification/${uid}/action`, { action, reason: r });
      toast.success(`Verification: ${action}`);
      setReason({ ...reason, [uid]: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="p-6" data-testid="hq-verification">
      <p className="overline">Verification queue</p>
      <h1 className="font-display text-3xl tracking-tighter mb-6">Verification cases</h1>
      <div className="flex gap-2 mb-4 flex-wrap">
        {STATES.map((s) => (
          <button key={s} data-testid={`hq-verif-filter-${s}`} onClick={() => setState(s)}
                  className={`chip text-[10px] ${state === s ? "border-gold text-gold bg-gold/5" : ""}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>
      <div className="space-y-3">
        {items.length === 0 && <div className="border border-cream/10 p-4 text-center text-cream/50 text-xs" data-testid="hq-verif-empty">No cases in this state.</div>}
        {items.map((v) => (
          <div key={v.user_id} className="border border-cream/10 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`hq-verif-${v.user_id}`}>
            <div>
              <p className="font-display text-lg">{v.user_name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">{v.user_email} · {v.studio_name || "—"}</p>
              <p className="text-[10px] text-cream/50 mt-1">Submitted {v.submitted_at ? new Date(v.submitted_at).toLocaleString() : "—"}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Verification is simulated in this prototype — no real ID checks occurred.</p>
              {v.reason && <p className="text-[10px] text-cream/70 mt-1 italic">"{v.reason}"</p>}
            </div>
            {v.status === "pending" && (
              <div className="flex flex-col gap-2 md:min-w-[280px]">
                <input placeholder="Reason (for reject / resubmission)"
                       data-testid={`hq-verif-reason-${v.user_id}`}
                       value={reason[v.user_id] || ""} onChange={(e) => setReason({ ...reason, [v.user_id]: e.target.value })}
                       className="bg-transparent border border-cream/15 px-2 py-1 text-xs focus:border-gold outline-none" />
                <div className="flex gap-2">
                  <button data-testid={`hq-verif-verify-${v.user_id}`} onClick={() => act(v.user_id, "mark_verified")} className="btn-gold px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold">Verify</button>
                  <button data-testid={`hq-verif-resubmit-${v.user_id}`} onClick={() => act(v.user_id, "request_resubmission")} className="btn-ghost px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em]">Request resubmission</button>
                  <button data-testid={`hq-verif-reject-${v.user_id}`} onClick={() => act(v.user_id, "reject")} className="btn-ghost px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] text-red-300 border-red-300/30">Reject</button>
                </div>
              </div>
            )}
            {v.status !== "pending" && (
              <span className={`chip text-[10px] ${v.status === "verified" ? "border-emerald-400/40 text-emerald-300" : v.status === "rejected" ? "border-red-400/40 text-red-300" : ""}`}>{v.status.replace(/_/g, " ")}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
